/**
 * ws-client.ts — Socket.IO client factory for the Maloum CRM gateway.
 *
 * Security (T-04-33):
 *   - Connects to OUR NestJS gateway only (not wss://ws.maloum.com)
 *   - Authenticated via the httpOnly session cookie (withCredentials: true)
 *   - No Maloum URL is referenced here
 *
 * Typed subscriptions for @crm/types/socket events:
 *   - health:update        → HealthUpdate
 *   - validation:progress  → ValidationProgress
 *   - inbox:delta          → InboxDelta
 */

import { io, type Socket } from 'socket.io-client';
import type { HealthUpdate, ValidationProgress, InboxDelta } from '@crm/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UnsubFn = () => void;

export interface WsClient {
  /** Subscribe to per-account health FSM updates. */
  onHealthUpdate: (handler: (evt: HealthUpdate) => void) => UnsubFn;
  /** Subscribe to bulk-onboarding credential validation progress. */
  onValidationProgress: (handler: (evt: ValidationProgress) => void) => UnsubFn;
  /** Subscribe to inbox delta events (admin observability). */
  onInboxDelta: (handler: (evt: InboxDelta) => void) => UnsubFn;
  /** Disconnect and clean up. */
  disconnect: () => void;
  /** The underlying socket (for advanced use). */
  socket: Socket;
}

// ---------------------------------------------------------------------------
// Singleton socket (one connection per admin session)
// ---------------------------------------------------------------------------

let _socket: Socket | null = null;

function getSocket(): Socket {
  if (_socket && _socket.connected) return _socket;
  // Connect to the NestJS gateway (same origin in production; proxied in dev)
  _socket = io('/', {
    path: '/socket.io',
    // httpOnly cookie is sent automatically by the browser via withCredentials
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
  });
  return _socket;
}

// ---------------------------------------------------------------------------
// WsClient factory
// ---------------------------------------------------------------------------

export function createWsClient(): WsClient {
  const socket = getSocket();

  function on<T>(event: string, handler: (evt: T) => void): UnsubFn {
    socket.on(event, handler as (...args: unknown[]) => void);
    return () => socket.off(event, handler as (...args: unknown[]) => void);
  }

  return {
    socket,
    onHealthUpdate: (handler) => on<HealthUpdate>('health:update', handler),
    onValidationProgress: (handler) =>
      on<ValidationProgress>('validation:progress', handler),
    onInboxDelta: (handler) => on<InboxDelta>('inbox:delta', handler),
    disconnect: () => {
      socket.disconnect();
      _socket = null;
    },
  };
}
